import { z } from '@hono/zod-openapi';
import {
  MIN_VOLUNTEER_AGE,
  MAX_VOLUNTEER_AGE,
  getAge
} from '@goodneighbor/utilities/date/index';

export const usPhoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .refine((val) => !val.includes('_'), 'Please enter a complete phone number')
  .refine(
    (val) => /^\+1 \([2-9]\d{2}\) [2-9]\d{2}-\d{4}$/.test(val),
    'Invalid phone number'
  );

export const MIN_BIRTHDATE_YEAR = 1900;

export const birthDateSchema = z
  .string({ required_error: 'Please enter all digits of your date of birth' })
  .nullable()
  .superRefine((val, ctx) => {
    if (val === null || val === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter all digits of your date of birth'
      });
      return z.NEVER;
    }

    const date = new Date(val);
    if (isNaN(date.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter all digits of your date of birth'
      });
      return z.NEVER;
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (date > now) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Date of birth cannot be in the future'
      });
      return z.NEVER;
    }

    const age = getAge(date);

    if (age < MIN_VOLUNTEER_AGE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must be at least 12 years old to register'
      });
      return z.NEVER;
    }

    if (age > MAX_VOLUNTEER_AGE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date of birth'
      });
      return z.NEVER;
    }
  });

export const objectTypeSchema = z.enum([
  'NOMINATION',
  'PROFILE',
  'NEED',
  'EVENT',
  'DAILY_GOOD',
  'GOOD_VIBE',
  'CHALLENGE',
  'RAFFLE',
  'CHALLENGE_GROUP',
  'STREAK',
  'STRUCTURE',
  'SHARE',
  'MERCHANDISE',
  'NONE'
]);

export type ObjectType = z.infer<typeof objectTypeSchema>;

export const PASSWORD_MIN_LENGTH = 8;

// password regex
export const passwordRegex = {
  uppercase: /(?=.*[A-Z])/,
  lowercase: /(?=.*[a-z])/,
  number: /(?=.*\d)/,
  specialChar: /(?=.*[!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?~])/,
  noWhitespace: /^\S*$/
};
